import { type ReportRangeArgs } from '@fluxter/shared';
import { cn } from '@/lib/utils';

/**
 * Reports redesign — date-range picker that lives at the top of the
 * billable summary. Glass card with preset chips on the left, From
 * + To inputs in the middle, and a "N days" meta label on the right.
 *
 * The presets compute concrete `YYYY-MM-DD` from/to pairs based on
 * `referenceDate` (defaults to today). All presets follow the
 * backend's half-open semantics: `from <= started_at < to`. Custom
 * picks are signaled by a non-null `from`/`to` that doesn't match
 * any preset (the parent decides; this component just renders).
 */
export type RangePresetId =
  | 'today'
  | 'week'
  | 'month'
  | 'lastmonth'
  | 'quarter'
  | 'custom';

const PRESETS: ReadonlyArray<{ id: Exclude<RangePresetId, 'custom'>; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'lastmonth', label: 'Last month' },
  { id: 'quarter', label: 'This quarter' },
];

const fmtIso = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/**
 * Compute the {from, to} pair for a preset id, anchored to UTC days
 * (matching `ReportRange`'s half-open semantics).
 */
export const rangeForPreset = (
  preset: Exclude<RangePresetId, 'custom'>,
  reference: Date = new Date(),
): ReportRangeArgs => {
  const today = new Date(
    Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), reference.getUTCDate()),
  );
  const dow = today.getUTCDay(); // 0 (Sun) … 6 (Sat)
  const tomorrow = new Date(today.getTime() + 86_400_000);

  if (preset === 'today') {
    return { from: fmtIso(today), to: fmtIso(tomorrow) };
  }
  if (preset === 'week') {
    // Week starts Mon (matches calendar). Mon=0 offset.
    const mondayOffset = (dow + 6) % 7;
    const monday = new Date(today.getTime() - mondayOffset * 86_400_000);
    const nextMonday = new Date(monday.getTime() + 7 * 86_400_000);
    return { from: fmtIso(monday), to: fmtIso(nextMonday) };
  }
  if (preset === 'month') {
    const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    const nextFirst = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
    return { from: fmtIso(first), to: fmtIso(nextFirst) };
  }
  if (preset === 'lastmonth') {
    const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    const thisFirst = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    return { from: fmtIso(first), to: fmtIso(thisFirst) };
  }
  // quarter — calendar quarter (Jan-Mar / Apr-Jun / Jul-Sep / Oct-Dec)
  const qStartMonth = Math.floor(today.getUTCMonth() / 3) * 3;
  const qFirst = new Date(Date.UTC(today.getUTCFullYear(), qStartMonth, 1));
  const nextQFirst = new Date(Date.UTC(today.getUTCFullYear(), qStartMonth + 3, 1));
  return { from: fmtIso(qFirst), to: fmtIso(nextQFirst) };
};

/**
 * Detect which preset (if any) matches the current range. Used to
 * highlight the active preset chip when the parent restores from
 * persisted state.
 */
export const matchPreset = (range: ReportRangeArgs): RangePresetId => {
  for (const p of PRESETS) {
    const r = rangeForPreset(p.id);
    if (r.from === range.from && r.to === range.to) return p.id;
  }
  return 'custom';
};

const dayCountOf = (range: ReportRangeArgs): number => {
  if (!range.from || !range.to) return 0;
  const a = Date.parse(range.from);
  const b = Date.parse(range.to);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
};

export interface ReportsRangePickerProps {
  range: ReportRangeArgs;
  onChange: (next: ReportRangeArgs) => void;
}

export const ReportsRangePicker = ({ range, onChange }: ReportsRangePickerProps) => {
  const preset = matchPreset(range);
  const days = dayCountOf(range);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3.5 rounded-xl border p-3 backdrop-blur-md',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
      )}
    >
      <span
        role="tablist"
        className={cn(
          'inline-flex items-center gap-0.5 rounded-[9px] border p-[3px]',
          'border-border bg-secondary',
        )}
      >
        {PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(rangeForPreset(p.id))}
              className={cn(
                'inline-flex h-6 cursor-pointer items-center rounded-md border-0 px-2.5 text-[11.5px] font-medium',
                active
                  ? 'bg-background text-foreground shadow-[0_1px_2px_oklch(0_0_0/0.06)]'
                  : 'text-foreground-muted hover:text-foreground bg-transparent',
              )}
            >
              {p.label}
            </button>
          );
        })}
      </span>

      <span aria-hidden="true" className="bg-[var(--glass-card-border)] h-[22px] w-px" />

      <span className="inline-flex flex-col gap-1">
        <span className="text-foreground-faint font-mono text-[10px] font-semibold uppercase tracking-[0.06em]">
          From
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5',
            'h-7 border-border bg-secondary text-foreground font-mono text-[12px] tabular-nums',
          )}
        >
          <input
            type="date"
            value={range.from ?? ''}
            onChange={(e) => onChange({ from: e.target.value, to: range.to })}
            className="text-foreground w-[112px] border-0 bg-transparent font-mono text-[12px] tabular-nums outline-none"
          />
        </span>
      </span>

      <span className="text-foreground-faint mt-3.5">→</span>

      <span className="inline-flex flex-col gap-1">
        <span className="text-foreground-faint font-mono text-[10px] font-semibold uppercase tracking-[0.06em]">
          To
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5',
            'h-7 border-border bg-secondary text-foreground font-mono text-[12px] tabular-nums',
          )}
        >
          <input
            type="date"
            value={range.to ?? ''}
            onChange={(e) => onChange({ from: range.from, to: e.target.value })}
            className="text-foreground w-[112px] border-0 bg-transparent font-mono text-[12px] tabular-nums outline-none"
          />
        </span>
      </span>

      <span
        className={cn(
          'text-foreground-faint ml-auto inline-flex items-center gap-1.5 font-mono text-[11.5px]',
        )}
      >
        <strong className="text-foreground-muted font-medium">{days}</strong>
        days · excludes end
      </span>
    </div>
  );
};
